import { useEffect, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { postRequest } from "../../../constants/api";
import { Loader, SearchBar } from "../../../components";
import { FetchErrorHandling } from "../../../components/FetchErrorHandling";
import { Container, Card, Form, Row, Col } from "react-bootstrap";
import { Colors } from "../../../constants/styles";
import { formatDate } from "../../../constants/helper";
import {
  Box,
  Paper,
  Alert,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat } from "react-number-format";
import useScreenWidth from "../../../hooks/useScreenWidth";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
  root: {
    "& .MuiTableCell-head": {
      color: "white",
      backgroundColor: Colors.blue700,
    },
  },
  tableRightBorder: {
    borderWidth: 0,
    borderRightWidth: 1,
    borderColor: "white",
    borderStyle: "solid",
  },
});

export function ShowTablePenerimaanAngsuranKelompok({
  currentPosts,
  angPerBulan,
  onDeleteRow,
}) {
  const classes = useStyles();

  return (
    <TableContainer component={Paper} sx={{ width: "100%" }}>
      <Table aria-label="simple table">
        <TableHead className={classes.root}>
          <TableRow>
            <TableCell
              sx={{ fontWeight: "bold" }}
              className={classes.tableRightBorder}
            >
              No.
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold" }}
              className={classes.tableRightBorder}
            >
              Nama
            </TableCell>
            <TableCell
              sx={{ fontWeight: "bold" }}
              className={classes.tableRightBorder}
              align="right"
            >
              Bayar
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} align="center">
              Aksi
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {(currentPosts || []).map((row, index) => (
            <TableRow
              key={row.id || row._id || index}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                "&:hover": { bgcolor: Colors.grey300 },
              }}
            >
              <TableCell component="th" scope="row">
                {index + 1}
              </TableCell>

              <TableCell>{row?.peserta?.namaPeserta}</TableCell>

              <TableCell align="right">
                {(Number(angPerBulan) || 0).toLocaleString("en-US")}
              </TableCell>

              <TableCell align="center">
                <Tooltip title="Hapus baris ini">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      const id = row.id || row._id;
                      if (!id) return;
                      onDeleteRow?.(id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TambahSetoranAngsuranKelompok = () => {
  const screenSize = useScreenWidth();
  const [isFetchError, setIsFetchError] = useState(false);
  const { dispatch } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [validated, setValidated] = useState(false);

  // Angsuran
  const [noKwitansi, setNoKwitansi] = useState("");
  const [angsuranKelompokId, setAngsuranKelompokId] = useState("");
  const [no, setNo] = useState("");
  const [tglJatuhTempo, setTglJatuhTempo] = useState("");
  const [angPerBulan, setAngPerBulan] = useState("");
  const [totalAngsuran, setTotalAngsuran] = useState(""); // stays fixed once calculated
  const [tglBayar, setTglBayar] = useState("");
  const [bayar, setBayar] = useState("");

  const [kelompoks, setKelompoks] = useState([]);
  const [kelompokId, setKelompokId] = useState("");
  const [noKelompok, setNoKelompok] = useState("");
  const [openKelompok, setOpenKelompok] = useState(false);
  const [searchTermKelompok, setSearchTermKelompok] = useState("");
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // rows shown on UI (user can delete some)
  const [penerimaanAngsuranKelompoks, setPenerimaanAngsuranKelompoks] =
    useState([]);

  let [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [pages, setPages] = useState(0);
  const [rows, setRows] = useState(0);
  const [query, setQuery] = useState("");

  const classes = useStyles();

  const handleChange = (e, p) => setPage(p - 1);

  const searchData = (e) => {
    e.preventDefault();
    setPage(0);
    setSearchTermKelompok(query);
  };

  const handleClickOpenKelompok = () => setOpenKelompok(true);
  const handleCloseKelompok = () => setOpenKelompok(false);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // only update BAYAR when rows deleted; totalAngsuran stays fixed
  const recomputeBayarOnly = useCallback(
    (rowsList) => {
      const perBulan = parseInt(angPerBulan || 0, 10) || 0;
      const total = (rowsList?.length || 0) * perBulan;
      setBayar(total);
    },
    [angPerBulan],
  );

  const handleDeleteRow = (rowId) => {
    const updated = (penerimaanAngsuranKelompoks || []).filter(
      (x) => (x.id || x._id) !== rowId,
    );
    setPenerimaanAngsuranKelompoks(updated);
    recomputeBayarOnly(updated); // only bayar changes
  };

  useEffect(() => {
    getSetoranAngsuranKelompokNextKode();
    getKelompoksFirstTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getKelompoks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTermKelompok]);

  const getSetoranAngsuranKelompokNextKode = async () => {
    const response = await postRequest(
      `/setoranAngsuranKelompokNextKode`,
      {},
      navigate,
      dispatch,
    );
    setNoKwitansi(response.data);
  };

  const getKelompoks = async () => {
    const response = await postRequest(
      `/kelompoksBelumLunasPagination?search_query=${searchTermKelompok}&page=${page}&limit=${limit}`,
      {},
      navigate,
      dispatch,
    );
    setQuery(searchTermKelompok);
    setKelompoks(response.data.kelompoks);
    setPage(response.data.page);
    setPages(response.data.totalPage);
    setRows(response.data.totalRows);
  };

  const getKelompoksFirstTime = async () => {
    const response = await postRequest(
      `/kelompoksBelumLunasPagination?search_query=${searchTermKelompok}&page=${page}&limit=${limit}`,
      {},
      navigate,
      dispatch,
    );
    setQuery(searchTermKelompok);
    setKelompoks(response.data.kelompoks);
    setPage(response.data.page);
    setPages(response.data.totalPage);
    setRows(response.data.totalRows);
  };

  const findNoAngsuran = async (kelompokIdParam) => {
    try {
      setLoading(true);
      const response = await postRequest(
        `/angsuranKelompokTerakhirByKelompok`,
        { kelompokId: kelompokIdParam },
        navigate,
        dispatch,
      );

      setAngsuranKelompokId(response.data.id);
      setTglJatuhTempo(formatDate(response.data.tglJatuhTempo));
      setNo(response.data.no);
      setAngPerBulan(response.data.angPerBulan);

      setLoading(false);
    } catch (err) {
      setIsFetchError(true);
      setLoading(false);
    }
  };

  const pesertaKelompoksForBayarAngsuran = async () => {
    try {
      setLoading(true);
      const response = await postRequest(
        `/pesertaKelompoksForBayarAngsuran`,
        { kelompokId, angsuranKelompokId },
        navigate,
        dispatch,
      );

      const rowsList = response.data || [];
      setPenerimaanAngsuranKelompoks(rowsList);

      const findPesertaKelompoks = await postRequest(
        `/userPesertaKelompoksByKelompok`,
        { kelompokId, angsuranKelompokId },
        navigate,
        dispatch,
      );

      // initial compute: totalAngsuran and bayar are set ONCE at "Cari"
      const perBulan = parseInt(angPerBulan || 0, 10) || 0;
      const initialTotal = (findPesertaKelompoks.data.length || 0) * perBulan;
      const initialBayar = (rowsList.length || 0) * perBulan;
      setTotalAngsuran(initialTotal);
      setBayar(initialBayar);

      setLoading(false);
    } catch (err) {
      setIsFetchError(true);
      setLoading(false);
    }
  };

  const saveTransaksiSetoranAngsuranKelompok = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const form = e.currentTarget;

    if (form.checkValidity()) {
      setLoading(true);
      try {
        await postRequest(
          `/saveTransaksiSetoranAngsuranKelompok`,
          {
            angsuranKelompokId,
            kelompokId,
            no,
            angPerBulan,
            tglBayar,
            totalAngsuran, // stays fixed
            bayar, // reflects after deletions (optional behavior)
            penerimaanAngsuranKelompoks, // only rows remaining
          },
          navigate,
          dispatch,
        );
        setLoading(false);
        navigate("/setoran");
      } catch (err) {
        setLoading(false);
        alert(err?.response?.data?.message || "Terjadi kesalahan");
      }
    } else {
      setError(true);
      setOpen(true);
    }
    setValidated(true);
  };

  if (loading) return <Loader />;
  if (isFetchError) return <FetchErrorHandling />;

  const textRight = { textAlign: screenSize >= 650 && "right" };

  return (
    <Container>
      <h3>Arisan</h3>
      <h5 style={{ fontWeight: 400 }}>Tambah Setoran Angsuran Kelompok</h5>
      <hr />

      <Form
        noValidate
        validated={validated}
        onSubmit={saveTransaksiSetoranAngsuranKelompok}
      >
        <Card>
          <Card.Header>Setoran Angsuran Kelompok</Card.Header>
          <Card.Body>
            <Row>
              <Col sm={6}>
                <Form.Group
                  as={Row}
                  className="mb-3"
                  controlId="formPlaintextPassword"
                >
                  <Form.Label column sm="5" style={textRight}>
                    No. Kwitansi :
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control value={noKwitansi} disabled readOnly />
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    No. Kelompok <b style={colorRed}>*</b> :
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control
                      required
                      value={noKelompok}
                      readOnly
                      placeholder="Pilih..."
                      onClick={handleClickOpenKelompok}
                      isInvalid={noKelompok.length === 0 && true}
                    />
                    <Form.Control.Feedback type="invalid">
                      Kode Kelompok harus diisi!
                    </Form.Control.Feedback>
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    Tgl. Jatuh Tempo :
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control disabled value={tglJatuhTempo} />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    Tgl. Bayar <b style={colorRed}>*</b> :
                  </Form.Label>
                  <Col sm="7">
                    <DatePicker
                      dateFormat="dd/MM/yyyy"
                      customInput={<Form.Control required />}
                      selected={tglBayar}
                      onChange={(date) => setTglBayar(date)}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    No :
                  </Form.Label>
                  <Col sm="7">
                    <Form.Control disabled value={no} />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    Setoran/Bulan :
                  </Form.Label>
                  <Col sm="7">
                    <NumericFormat
                      disabled
                      value={angPerBulan}
                      thousandSeparator
                      customInput={Form.Control}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    Total :
                  </Form.Label>
                  <Col sm="7">
                    <NumericFormat
                      disabled
                      value={totalAngsuran}
                      thousandSeparator
                      customInput={Form.Control}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col sm={6}>
                <Form.Group as={Row} className="mb-3">
                  <Form.Label column sm="5" style={textRight}>
                    Bayar :
                  </Form.Label>
                  <Col sm="7">
                    <NumericFormat
                      disabled
                      value={bayar}
                      thousandSeparator
                      customInput={Form.Control}
                    />
                  </Col>
                </Form.Group>
              </Col>
            </Row>

            <Box sx={spacingTop}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={pesertaKelompoksForBayarAngsuran}
                sx={{ marginRight: 2 }}
              >
                Cari
              </Button>
            </Box>
          </Card.Body>
        </Card>

        <Box sx={tableContainer}>
          <ShowTablePenerimaanAngsuranKelompok
            currentPosts={penerimaanAngsuranKelompoks}
            angPerBulan={angPerBulan}
            onDeleteRow={handleDeleteRow}
          />
        </Box>

        <Box sx={spacingTop}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => navigate("/setoran")}
            sx={{ marginRight: 2 }}
          >
            {"< Kembali"}
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} type="submit">
            Simpan
          </Button>
        </Box>
      </Form>

      {error && (
        <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={alertBox}>
            Data belum terisi semua!
          </Alert>
        </Snackbar>
      )}

      <Dialog
        open={openKelompok}
        onClose={handleCloseKelompok}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">{`Pilih Data Kelompok`}</DialogTitle>
        <DialogActions>
          <Box sx={dialogContainer}>
            <Form onSubmit={searchData}>
              <Box sx={searchBarContainer}>
                <SearchBar value={query} setSearchTerm={setQuery} />
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disableElevation
                >
                  Cari
                </Button>
              </Box>
            </Form>

            <TableContainer component={Paper} sx={dialogWrapper}>
              <Table aria-label="simple table">
                <TableHead className={classes.root}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold" }}
                      className={classes.tableRightBorder}
                    >
                      No. Kelompok
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Tgl.</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {kelompoks.map((k, index) => (
                    <TableRow
                      key={k.id || k._id || index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": { bgcolor: Colors.grey300 },
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setNoKelompok(k.noKelompok);
                        setKelompokId(k.id);
                        findNoAngsuran(k.id);
                        handleCloseKelompok();
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {k.noKelompok}
                      </TableCell>
                      <TableCell>{formatDate(k.tglKelompok)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={tableContainer}>
              <Pagination
                count={pages}
                page={page + 1}
                onChange={handleChange}
                color="primary"
                size={screenSize <= 600 ? "small" : "large"}
              />
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TambahSetoranAngsuranKelompok;

// ===== styles =====
const colorRed = { color: "red" };
const spacingTop = { mt: 4 };
const alertBox = { width: "100%" };

const dialogContainer = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
};

const dialogWrapper = {
  width: "100%",
  marginTop: 2,
};

const tableContainer = {
  pt: 4,
  display: "flex",
  justifyContent: "center",
};

const searchBarContainer = {
  pt: 6,
  display: "flex",
  justifyContent: "center",
};
